import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Users, 
  Calendar,
  Clock,
  ExternalLink
} from 'lucide-react';

const ZOOM_LINK = 'https://warriorsage.zoom.us/j/89699040836?pwd=fz7czsfvauOQ664TIvuEjb1xZanb6r.1';
const SESSION_TIME = {
  hour: 10, // 10am PT
  minute: 0
};

// Check if a date is a holiday that should be skipped
const isHoliday = (year, month, day) => {
  // Christmas - December 25
  if (month === 12 && day === 25) {
    return true;
  }
  
  // New Year's Day - January 1
  if (month === 1 && day === 1) {
    return true;
  }
  
  // Thanksgiving - 4th Thursday of November
  if (month === 11) {
    // Find the first Thursday of November
    const firstDay = new Date(year, 10, 1); // month is 0-indexed, so 10 = November
    const firstThursday = firstDay.getDay() === 4 
      ? 1 
      : (4 - firstDay.getDay() + 7) % 7 + 1;
    
    // 4th Thursday would be firstThursday + 21 days
    const fourthThursday = firstThursday + 21;
    
    if (day === fourthThursday) {
      return true;
    }
  }
  
  return false;
};

const MentorshipSessionWidget = () => {
  // Store date as an object with year, month, day to avoid timezone issues
  const [nextSessionDate, setNextSessionDate] = useState(null);
  const [nextSessionTimestamp, setNextSessionTimestamp] = useState(null);

  useEffect(() => {
    calculateNextSession();
  }, []);

  const calculateNextSession = () => {
    // Get current time
    const now = new Date();
    
    // Get current day of week and time in Pacific Time
    const ptFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      weekday: 'long'
    });
    
    const ptParts = ptFormatter.formatToParts(now);
    const ptYear = parseInt(ptParts.find(p => p.type === 'year').value);
    const ptMonth = parseInt(ptParts.find(p => p.type === 'month').value); // Keep as 1-indexed for date calculation
    const ptDay = parseInt(ptParts.find(p => p.type === 'day').value);
    const ptHour = parseInt(ptParts.find(p => p.type === 'hour').value);
    const ptDayOfWeek = ptParts.find(p => p.type === 'weekday').value.toLowerCase();
    
    // Determine next Thursday
    const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayIndex = daysOfWeek.indexOf(ptDayOfWeek);
    let daysToAdd = 0;
    
    if (currentDayIndex === 4) {
      // Today is Thursday
      if (ptHour >= SESSION_TIME.hour) {
        // Past 10am PT, get next Thursday
        daysToAdd = 7;
      } else {
        // Before 10am PT, use today
        daysToAdd = 0;
      }
    } else {
      // Calculate days until next Thursday
      daysToAdd = (4 - currentDayIndex + 7) % 7;
      if (daysToAdd === 0) daysToAdd = 7;
    }
    
    // Calculate the session date in PT
    // Create a temporary date object to calculate the next Thursday
    // We'll work with PT date components directly to avoid timezone shifts
    let tempDate = new Date(ptYear, ptMonth - 1, ptDay); // month is 0-indexed in Date constructor
    tempDate.setDate(tempDate.getDate() + daysToAdd);
    
    // Extract date components - these represent the PT date, not local time
    let sessionYear = tempDate.getFullYear();
    let sessionMonth = tempDate.getMonth() + 1; // Convert back to 1-indexed
    let sessionDay = tempDate.getDate();
    
    // Skip holidays by moving to the next Thursday if the calculated date is a holiday
    // Limit to checking up to 4 weeks ahead to avoid infinite loops
    let attempts = 0;
    while (isHoliday(sessionYear, sessionMonth, sessionDay) && attempts < 4) {
      // Move to next Thursday (add 7 days)
      tempDate.setDate(tempDate.getDate() + 7);
      sessionYear = tempDate.getFullYear();
      sessionMonth = tempDate.getMonth() + 1;
      sessionDay = tempDate.getDate();
      attempts++;
    }
    
    // Store date components separately to avoid timezone conversion issues
    setNextSessionDate({ year: sessionYear, month: sessionMonth, day: sessionDay });
    
    // Also create a timestamp for the countdown (10am PT on that date)
    // We'll create this carefully to represent 10am PT
    // Create date in PT by constructing it as if it's in local time, but we know it's PT
    const sessionDateLocal = new Date(sessionYear, sessionMonth - 1, sessionDay, SESSION_TIME.hour, SESSION_TIME.minute, 0, 0);
    setNextSessionTimestamp(sessionDateLocal);
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return '';
    
    // Format date using the stored components to avoid timezone issues
    // This matches the pattern used in VideoLibrary.jsx
    const date = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = () => {
    // Session is always at 10am PT / 12pm CST / 1pm EST
    // Note: During DST transitions, these may shift slightly, but for display purposes
    // we'll show the standard times
    return '10:00 AM PT / 12:00 PM CST / 1:00 PM EST';
  };

  const isToday = (dateObj) => {
    if (!dateObj) return false;
    
    // Get current date in PT to compare
    const now = new Date();
    const ptFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    const ptParts = ptFormatter.formatToParts(now);
    const ptYear = parseInt(ptParts.find(p => p.type === 'year').value);
    const ptMonth = parseInt(ptParts.find(p => p.type === 'month').value);
    const ptDay = parseInt(ptParts.find(p => p.type === 'day').value);
    
    return dateObj.year === ptYear && dateObj.month === ptMonth && dateObj.day === ptDay;
  };

  const getTimeUntilSession = (timestamp) => {
    if (!timestamp) return null;
    
    const now = new Date();
    const diff = timestamp - now;
    
    if (diff < 0) return null; // Session passed
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return `${days} ${days === 1 ? 'day' : 'days'} ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    } else if (hours > 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    } else {
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }
  };

  if (!nextSessionDate) {
    return null;
  }

  const timeUntil = getTimeUntilSession(nextSessionTimestamp);

  return (
    <Card className="hover:shadow-xl transition-all duration-300 rounded-3xl border-0 bg-gradient-to-br from-indigo-50 to-indigo-100 shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Users className="w-7 h-7 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl text-gray-900">Founder Mentorship Session</CardTitle>
              <CardDescription className="text-gray-600">Weekly group session</CardDescription>
            </div>
          </div>
          <Calendar className="w-6 h-6 text-indigo-600" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <Clock className="w-4 h-4 text-indigo-600" />
            <p className="text-sm font-medium text-gray-700">
              {isToday(nextSessionDate) ? 'Today' : 'Next Session'}
            </p>
          </div>
          <p className="text-lg font-semibold text-gray-900 mb-1">
            {formatDate(nextSessionDate)}
          </p>
          <p className="text-sm text-gray-600 mb-2">
            {formatTime()}
          </p>
          {timeUntil && (
            <div className="flex items-center space-x-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg inline-flex">
              <Clock className="w-3 h-3" />
              <span>{timeUntil} until session</span>
            </div>
          )}
        </div>
        
        <Button 
          onClick={() => window.open(ZOOM_LINK, '_blank', 'noopener,noreferrer')}
          className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white h-12 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
        >
          <Users className="w-5 h-5 mr-2" />
          Join Zoom Call
          <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default MentorshipSessionWidget;
